// =============================================================================
// BookingCellV2.tsx — Bookings V2 grid cell (Member Profile V2 "Fortius" look).
//
// Same contract as the V1 BookingCell:
//   - identical props (slot, booking, bookingIntervalMins, facilityName, onCellClick)
//   - MUI <Box> + sx (the grid is MUI — do NOT switch to CSS classes)
//   - rowSpan-aware height, same permission gate, same testId + data-* attrs
//
// Fortius language (driven by bookingTokens.ts — the FORT-178 seam):
//   - teal accent + ink/muted/line neutrals + success/warning/error status trio
//   - a 4px left status stripe on each booking card (the V2 `.ft-stripe` device)
//   - system font, no serif/mono — only size/weight vary
//   - member name on top, party-size + price + payment tag (radius 6) on the
//     bottom row; price via the existing useFormatCurrency() hook (locale-aware)
// =============================================================================

import React from 'react';
import { Box, Tooltip } from '@mui/material';
import moment from 'moment-timezone';
import { Slot } from '../../../model/bookings/Slot';
import { Booking } from '../../../model/bookings/Booking';
import { AdminPermissions } from '../../../model/auth/Permissions';
import { useMember } from '../../../common/member/MemberContext';
import { useClient } from '../../../common/ClientProvider';
import { useFormatCurrency } from '../../../hooks/useFormatters';
import {
  bookingColors,
  bookingRadii,
  pastStripe,
  paymentTagPalette,
  slotPalette,
} from './bookingTokens';
import {
  canMemberBypassAdvanceWindow,
  derivePartySize,
  derivePaymentTag,
  derivePrice,
  deriveSlotKind,
  deriveSlotPermissions,
  deriveSlotTitle,
  getBookingSpan,
  isBookingCardKind,
} from './bookingStatus';

interface BookingCellV2Props {
  slot: Slot;
  booking: Booking | null;
  facilityName: string;
  bookingIntervalMins: number;
  onCellClick: (booking: Booking | null, slot: Slot, facilityName: string) => void;
  // Whether the facility this column belongs to is currently bookable (FORT-401).
  // An inactive facility's EMPTY cells are inert — greyed, not-allowed, no Make
  // Booking — while cells holding an existing booking stay clickable so staff can
  // move or cancel work already on a court they have just taken out of service.
  // Defaults true so every existing caller keeps today's behaviour.
  facilityIsActive?: boolean;
  // When true (desktop grid), a booking card fills its rowSpan'd cell exactly
  // (height: 100%) instead of a fixed span*ROW_HEIGHT — so it doesn't drift out
  // of alignment as the 1px row dividers accumulate over a multi-interval span.
  // Mobile has no rowSpan, so the card must define its own height there.
  fillHeight?: boolean;
}

// Per-interval row height. Kept as compact as a single-interval booking card
// allows: it must still fit two rows of content (title + party/price/tag) at
// the tightened padding below without clipping. Must match ROW_HEIGHT in
// BookingScheduleTableV2 so booked and empty rows are the same height.
const ROW_HEIGHT_PX = 48;

export const BookingCellV2: React.FC<BookingCellV2Props> = ({
  slot,
  booking,
  bookingIntervalMins,
  facilityName,
  onCellClick,
  fillHeight = false,
  facilityIsActive = true,
}) => {
  const { permissions, currentMember } = useMember();
  const { currentClientId } = useClient();
  const formatCurrency = useFormatCurrency();

  const isAdmin = permissions.permissions.some(
    (p) =>
      p.name === AdminPermissions.ClubAdmin ||
      p.name === AdminPermissions.FortiusAdmin ||
      p.name === AdminPermissions.FrontOfHouse
  );

  // "The club" for the FORT-401 out-of-service rules — kept in lock-step with the
  // API's _bookingStaffPermissions in BookingValidationService. It adds ClubOwner to
  // the trio above: a club owner is the club's own management tier, and the API lets
  // them act on bookings on a closed court, so the grid must not lock them out of a
  // capability they actually have.
  const isBookingStaff =
    isAdmin || permissions.permissions.some((p) => p.name === AdminPermissions.ClubOwner);

  // The member advance-booking window only restricts *regular* members. Anyone
  // who isn't a regular member — staff (admin/front-of-house) or a non-Member
  // member type such as Coach — sees far-ahead empty slots as Available and can
  // book them. (This only lifts the advance-window block, not past-slot or
  // other-member's-booking access, which stay admin-only in deriveSlotPermissions.)
  const canBypassAdvanceWindow = canMemberBypassAdvanceWindow(
    isAdmin,
    currentMember?.memberType ?? 'Member'
  );

  const { isDisabled: slotIsDisabled, canClick: slotCanClick } = deriveSlotPermissions({
    isAdmin,
    canBypassAdvanceWindow,
    currentMembershipId: currentMember?.membershipId,
    slot,
    booking,
  });

  // Cells on an out-of-service facility, in two parts. [FORT-401]
  //
  //  - EMPTY slots are inert for everyone, staff included: the fix for a disabled
  //    court is re-enabling it in Settings, not clicking through. This outranks the
  //    admin bypass in deriveSlotPermissions.
  //  - Slots holding a BOOKING stay openable for staff, who have to clear the court,
  //    but are inert for members. The API refuses a member's cancel and change there,
  //    so opening the modal would only offer them buttons that 400 — the frontend gate
  //    has to match the server's, not sit looser than it (FORT-392/393).
  const isInactiveEmptySlot = !facilityIsActive && !booking;
  const isInactiveForMember = !facilityIsActive && !isBookingStaff;
  const isInertOutOfService = isInactiveEmptySlot || isInactiveForMember;
  const isDisabled = slotIsDisabled || isInertOutOfService;
  const canClick = slotCanClick && !isInertOutOfService;

  // Only EMPTY inert cells render as a plain grey block. A member's own booking on a
  // closed court still renders as its booking card — they should see that it exists,
  // they just can't act on it here.
  const kind = isInactiveEmptySlot
    ? 'blocked'
    : deriveSlotKind(slot, booking, canBypassAdvanceWindow, currentClientId);
  const colors = slotPalette[kind];
  const isBookingCard = isBookingCardKind(kind);

  const title = isInactiveEmptySlot
    ? 'Unavailable'
    : deriveSlotTitle(slot, booking, canBypassAdvanceWindow);
  const price = derivePrice(slot, booking);
  const partySize = derivePartySize(booking);
  const paymentTag = booking ? derivePaymentTag(booking, slot, currentClientId) : null;

  const isFirstSlotOfBooking =
    booking && booking.bookingDateTimeUtc.getTime() === slot.startTime.getTime();
  const shouldShowContent = !booking || isFirstSlotOfBooking;
  // Desktop (fillHeight): every cell — booked OR empty — fills its cell, so each
  // row's height is anchored solely by the time-gutter cell (ROW_HEIGHT). This
  // keeps booked and unbooked rows exactly the same height. Mobile has no rowSpan,
  // so the card must define its own height (span * ROW_HEIGHT).
  const height = fillHeight
    ? '100%'
    : booking
      ? `${getBookingSpan(booking, bookingIntervalMins) * ROW_HEIGHT_PX}px`
      : `${ROW_HEIGHT_PX}px`;

  const handleClick = () => {
    if (canClick) {
      onCellClick(booking, slot, facilityName);
    }
  };

  // Stable test id from facility + the slot's wall-clock start in its timezone.
  const hhmm = moment(new Date(slot.startTime)).tz(slot.timezone).format('HHmm');
  const testId = `booking-cell-v2-${facilityName.replace(/\s+/g, '-').toLowerCase()}-${hhmm}`;

  const cell = (
    <Box
      data-testid={testId}
      data-facility-active={facilityIsActive ? 'true' : 'false'}
      data-booking-status={booking?.bookingStatus ?? slot.bookingStatus}
      data-payment-status={booking?.paymentTransactionStatus ?? ''}
      data-member-name={booking?.memberName ?? ''}
      data-disabled={isDisabled ? 'true' : 'false'}
      onClick={handleClick}
      sx={{
        height,
        width: '100%',
        boxSizing: 'border-box',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        border: `1px solid ${colors.border}`,
        // V2 stripe device: a 4px status bar down the left of a booking card.
        borderLeft: isBookingCard ? `4px solid ${colors.stripe}` : `1px solid ${colors.border}`,
        borderRadius: bookingRadii.cell,
        transition: 'background-color 0.15s, box-shadow 0.15s',
        display: shouldShowContent ? 'flex' : 'none',
        flexDirection: 'column',
        justifyContent: isBookingCard ? 'space-between' : 'center',
        alignItems: isBookingCard ? 'stretch' : 'center',
        padding: isBookingCard ? '3px 9px 3px' : '4px',
        backgroundColor: colors.base,
        ...(kind === 'available' && { borderStyle: 'dashed' }),
        ...(kind === 'past' && { backgroundImage: pastStripe }),
        '&:hover': {
          backgroundColor: isDisabled ? colors.base : colors.hover,
          boxShadow: isDisabled ? 'none' : '0 2px 8px rgba(31,42,46,0.10)',
        },
        position: 'relative',
        overflow: 'hidden',
        opacity: isDisabled ? 0.72 : 1,
      }}
    >
      {!shouldShowContent ? null : isBookingCard ? (
        <>
          {/* Top: member / title */}
          <Box
            sx={{
              fontWeight: 700,
              fontSize: '0.8rem',
              letterSpacing: '-0.2px',
              lineHeight: 1.2,
              color: bookingColors.ink,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </Box>

          {/* Bottom: meta (party + price) left, payment tag right */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '6px',
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                fontSize: '0.72rem',
                fontWeight: 600,
                color: bookingColors.inkMuted,
                minWidth: 0,
              }}
            >
              {partySize != null && (
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}
                >
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden>
                    <circle cx="6" cy="4" r="2" stroke="currentColor" strokeWidth="1.2" />
                    <path
                      d="M2.5 10c.4-1.7 1.8-2.7 3.5-2.7s3.1 1 3.5 2.7"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                    />
                  </svg>
                  {partySize}
                </Box>
              )}
              {partySize != null && price != null && (
                <Box
                  component="span"
                  sx={{
                    width: '3px',
                    height: '3px',
                    borderRadius: '50%',
                    bgcolor: 'currentColor',
                    opacity: 0.4,
                  }}
                />
              )}
              {price != null && (
                <Box component="span" sx={{ color: bookingColors.ink, fontWeight: 700 }}>
                  {formatCurrency(price)}
                </Box>
              )}
            </Box>

            {paymentTag && (
              <Box
                component="span"
                sx={{
                  flex: '0 0 auto',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  height: '16px',
                  px: '8px',
                  borderRadius: bookingRadii.tag,
                  fontSize: '0.625rem',
                  fontWeight: 700,
                  letterSpacing: '0.2px',
                  textTransform: 'uppercase',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  color: paymentTagPalette[paymentTag].fg,
                  backgroundColor: paymentTagPalette[paymentTag].bg,
                  '&::before': {
                    content: '""',
                    width: '5px',
                    height: '5px',
                    borderRadius: '50%',
                    backgroundColor: 'currentColor',
                  },
                }}
              >
                {paymentTagPalette[paymentTag].label}
              </Box>
            )}
          </Box>
        </>
      ) : (
        // Stateless slots: available / past / blocked — single centred label
        <Box
          sx={{
            fontSize: '0.68rem',
            fontWeight: 600,
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
            color: colors.ink,
          }}
        >
          {title}
        </Box>
      )}
    </Box>
  );

  // Explain the dead click rather than leaving the cursor as the only signal.
  // Only the inert cases get a tooltip — a normal cell shouldn't grow one. A member
  // looking at their own booking on a closed court is told who to talk to.
  const inertReason = isInactiveEmptySlot
    ? `${facilityName} is not currently available for booking.`
    : `${facilityName} is currently out of service. Please contact the club about this booking.`;

  return isInertOutOfService ? (
    <Tooltip title={inertReason} placement="top">
      {cell}
    </Tooltip>
  ) : (
    cell
  );
};

export default BookingCellV2;
